// frontend/src/pages/dashboard/ClientPage.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, Box, Divider, Grid, Chip, useTheme, Fade,
  useMediaQuery, IconButton, Collapse, Stack, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, Skeleton, Tabs, Tab, Tooltip
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Assignment as AssignmentIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon, Search as SearchIcon, FilterList as FilterIcon,
  Close as CloseIcon, Refresh as RefreshIcon
} from "@mui/icons-material";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { clientAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

// Modern color palette
const COLORS = {
  primary: "#4361ee",
  secondary: "#3f37c9",
  success: "#4cc9f0",
  warning: "#f72585",
  info: "#4895ef",
  light: "#f8f9fa",
  dark: "#212529",
  gray: "#6c757d",
  cardBg: "#ffffff",
  surface: "#f0f4f8"
};

const initialForm = {
  name: "",
  email: "",
  role: "",
  phone: "",
  contact_person: "",
  address: "",
  billing_address: "",
  tax_id: "",
};

const ClientPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { user: authUser } = useAuth();

  // Permissions
  const canViewPage = authUser?.hasPermission('client.view.page');
  const canViewAll = authUser?.hasPermission('client.view');
  const canViewOwn = authUser?.hasPermission('client.view.own');
  const canCreate = authUser?.hasPermission('client.create');
  const canUpdate = authUser?.hasPermission('client.update');
  const canDelete = authUser?.hasPermission('client.delete');

  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  // ✅ Search & Filter States
  const [searchOpen, setSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const searchInputRef = React.useRef(null);

  // ✅ Focus search when expanded
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Handlers
  const handleOpen = useCallback((client = null) => {
    setEditing(client);
    setFormData(client ? { ...initialForm, ...client } : initialForm);
    setFormErrors({});
    setError("");
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    setFormData(initialForm);
    setFormErrors({});
  }, [saving]);

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = "Client name is required.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errors.email = "Invalid email format.";
    return errors;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) return setFormErrors(errors);
    setSaving(true);
    try {
      const payload = { ...formData };
      let updatedClient;
      if (editing) {
        const res = await clientAPI.update(editing.id, payload);
        updatedClient = res.data;
        setClients((prev) =>
          prev.map((c) => (c.id === editing.id ? updatedClient : c))
        );
      } else {
        const res = await clientAPI.create(payload);
        updatedClient = res.data;
        setClients((prev) => [updatedClient, ...prev]);
      }
      handleClose();
    } catch (err) {
      if (err.response?.status === 422)
        setFormErrors(err.response.data.errors || {});
      else setError(err.response?.data?.error || "Failed to save client.");
    } finally {
      setSaving(false);
    }
  }, [formData, editing, validateForm, handleClose]);

  const handleDelete = useCallback(
    async (id) => {
      if (!canDelete || !window.confirm("Are you sure you want to delete this client permanently?")) return;
      try {
        setClients((prev) => prev.filter((c) => c.id !== id));
        await clientAPI.delete(id);
      } catch {
        setError("Failed to delete client.");
        clientAPI.getAll().catch(() => { });
      }
    },
    [canDelete]
  );

  // Fetch clients — now respects own vs all
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      // Pass `own=1` if only own allowed
      const params = {};
      if (canViewOwn && !canViewAll) {
        params.own = 1;
      }
      const res = await clientAPI.getAll(params);
      setClients(res.data || []);
    } catch (err) {
      setError("Failed to load clients.");
    } finally {
      setLoading(false);
    }
  }, [canViewOwn, canViewAll]);

  useEffect(() => {
    if (canViewPage) {
      fetchClients();
    } else {
      setLoading(false);
    }
  }, [canViewPage, fetchClients]);

  const toggleCardExpansion = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // ✅ Get unique roles for filter dropdown
  const roleOptions = useMemo(() => {
    const roles = [...new Set(clients.map(c => c.role).filter(Boolean))];
    return ['all', ...roles];
  }, [clients]);

  // ✅ Filter clients based on search & filters
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Search filter
      const matchesSearch = !searchQuery || 
        client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.contact_person?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.phone?.includes(searchQuery);
      
      // Role filter
      const matchesRole = filterRole === 'all' || client.role === filterRole;
      
      return matchesSearch && matchesRole;
    });
  }, [clients, searchQuery, filterRole]);

  // ✅ Reset all filters
  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setFilterRole('all');
  }, []);

  // ✅ Render active filter chips
  const renderFilterChips = useCallback(() => {
    const chips = [];
    if (searchQuery) chips.push({ label: `Search: "${searchQuery}"`, onRemove: () => setSearchQuery('') });
    if (filterRole !== 'all') chips.push({ label: `Role: ${filterRole}`, onRemove: () => setFilterRole('all') });
    return chips;
  }, [searchQuery, filterRole]);

  // Mobile-friendly client cards
  const clientCards = useMemo(() => {
    if (clients.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            {canViewOwn && !canViewAll
              ? "No clients assigned to your projects"
              : "No clients found"}
          </Typography>
        </Box>
      );
    }

    return clients.map((client, i) => (
      <Paper
        key={client.id}
        variant="outlined"
        sx={{
          borderRadius: 2,
          mb: 2,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 2,
            borderColor: theme.palette.primary.main
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={() => toggleCardExpansion(client.id)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold'
            }}>
              {client.name.charAt(0).toUpperCase()}
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {client.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {client.contact_person || 'No contact person'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={String(i + 1).padStart(2, "0")}
              variant="outlined"
              sx={{ mr: 1 }}
            />
            <IconButton size="small">
              {expandedCard === client.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expandedCard === client.id}>
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={1.5}>
              {client.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{client.email}</Typography>
                </Box>
              )}
              {client.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">{client.phone}</Typography>
                </Box>
              )}
              {client.address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Typography variant="body2">{client.address}</Typography>
                </Box>
              )}
              {client.tax_id && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon fontSize="small" color="action" />
                  <Typography variant="body2">Tax ID: {client.tax_id}</Typography>
                </Box>
              )}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              {canUpdate && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpen(client);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(client.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Collapse>
      </Paper>
    ));
  }, [clients, expandedCard, handleOpen, handleDelete, canUpdate, canDelete, theme.palette]);

  // Desktop table rows
  const tableRows = useMemo(
    () =>
      clients.length > 0 ? (
        clients.map((client, i) => (
          <TableRow
            key={client.id}
            hover
            sx={{
              transition: "all 0.2s ease",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <TableCell sx={{ fontWeight: 500, color: "text.secondary" }}>
              {String(i + 1).padStart(2, "0")}
            </TableCell>
            <TableCell>
              <Typography fontWeight={600}>{client.name}</Typography>
            </TableCell>
            <TableCell>
              {client.email ? (
                <Typography variant="body2" color="text.secondary">
                  {client.email}
                </Typography>
              ) : (
                <Chip size="small" label="No Email" variant="outlined" />
              )}
            </TableCell>
            <TableCell>{client.phone || "—"}</TableCell>
            <TableCell>{client.contact_person || "—"}</TableCell>
            <TableCell>
  {client.role ? (
    <Chip 
      label={client.role} 
      size="small" 
      variant="outlined"
      sx={{ 
        borderColor: 'primary.main',
        color: 'primary.main',
        fontWeight: 500
      }}
    />
  ) : (
    <Typography variant="body2" color="text.secondary">—</Typography>
  )}
</TableCell>
            <TableCell>
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {client.address || "—"}
              </Typography>
            </TableCell>
            <TableCell>{client.tax_id || "—"}</TableCell>
            <TableCell align="right">
              {canUpdate && (
                <EditIcon
                  onClick={() => handleOpen(client)}
                  sx={{
                    cursor: "pointer",
                    color: theme.palette.primary.main,
                    mr: 1.5,
                    "&:hover": { color: theme.palette.primary.dark },
                  }}
                />
              )}
              {canDelete && (
                <DeleteIcon
                  onClick={() => handleDelete(client.id)}
                  sx={{
                    cursor: "pointer",
                    color: theme.palette.error.main,
                    "&:hover": { color: theme.palette.error.dark },
                  }}
                />
              )}
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
            <Typography color="text.secondary">
              {canViewOwn && !canViewAll
                ? "No clients assigned to your projects"
                : "No clients found"}
            </Typography>
          </TableCell>
        </TableRow>
      ),
    [clients, handleOpen, handleDelete, theme, canUpdate, canDelete, canViewOwn, canViewAll]
  );

  // ✅ Skeleton Loader for Client Cards (Mobile)
  const renderCardSkeletons = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {[1, 2, 3, 4].map((i) => (
        <Paper key={i} variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
          <Collapse in={true}>
            <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width="70%" height={16} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width="50%" height={16} />
                </Box>
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
              </Box>
            </Box>
          </Collapse>
        </Paper>
      ))}
    </Box>
  );

  // ✅ Skeleton Loader for Table (Desktop)
  const renderTableSkeletons = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            {['SL', 'Name', 'Email', 'Phone', 'Role', 'Contact', 'Address', 'TAX ID', 'Actions'].map((header, i) => (
              <TableCell key={i}>
                <Skeleton variant="text" width={header === 'Address' ? 120 : 60} height={24} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3, 4, 5].map((row) => (
            <TableRow key={row}>
              {[80, 100, 140, 100, 80, 120, 140, 100, 60].map((width, i) => (
                <TableCell key={i}>
                  <Skeleton variant="text" width={width} height={20} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {/* Header Skeleton */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 4, flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
            <Box>
              <Skeleton variant="text" width={200} height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={300} height={20} />
            </Box>
            <Skeleton variant="rounded" width={140} height={48} />
          </Box>
          
          {/* Search/Filter Bar Skeleton */}
          <Paper elevation={0} sx={{ width: '100%', p: 2, mb: 3, borderRadius: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Skeleton variant="rounded" width={40} height={40} />
              <Skeleton variant="rounded" width={120} height={40} />
              <Skeleton variant="rounded" width={120} height={40} />
              <Skeleton variant="rounded" width={40} height={40} />
            </Box>
          </Paper>
          
          {/* Content Skeletons */}
          {isMobile ? renderCardSkeletons() : renderTableSkeletons()}
        </Box>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <Box p={3}>
          <Alert severity="error">You do not have permission to access the Clients page.</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Fade in timeout={400}>
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {/* ✅ Header with Search & Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              mb: 4,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0
            }}
          >
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                {`${filteredClients.length} ${filteredClients.length === 1 ? "Client" : "Clients"}`}
              </Typography>
              {/* <Typography variant="body2" color="text.secondary">
                {canViewOwn && !canViewAll
                  ? "Clients assigned to your projects only"
                  : "Manage your clients and organizations efficiently"}
              </Typography> */}
            </Box>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpen()}
                sx={{
                  height: 48,
                  px: 3,
                  borderRadius: 3,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
                    transform: 'translateY(-1px)',
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                  '&:active': { transform: 'translateY(0)' },
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Add Client
              </Button>
            )}
          </Box>

          {/* ✅ Search & Filter Bar - TeamDetailsPage Style */}
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              p: { xs: 1.5, sm: 2 },
              mb: 3,
              borderRadius: 3,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {/* Toolbar Row */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 1.5 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              width: '100%'
            }}>
              {/* Left: Expandable Search */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
              }}>
                {!searchOpen ? (
                  <IconButton
                    onClick={() => setSearchOpen(true)}
                    size="medium"
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      width: 40,
                      height: 40,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderColor: 'primary.main'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                ) : (
                  <TextField
                    size="small"
                    inputRef={searchInputRef}
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => searchQuery ? setSearchQuery('') : setSearchOpen(false)}
                            sx={{ p: 0.5 }}
                          >
                            <CloseIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        '& fieldset': { borderColor: 'divider' },
                        '&:hover fieldset': { borderColor: 'primary.light' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                      }
                    }}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 } }}
                  />
                )}
              </Box>

              {/* Right: Desktop Filters */}
              <Box sx={{
                display: { xs: 'none', sm: 'flex' },
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
                ml: { sm: 'auto' }
              }}>
                <Autocomplete
                  size="small"
                  options={roleOptions}
                  value={filterRole === 'all' ? null : filterRole}
                  onChange={(e, newValue) => setFilterRole(newValue || 'all')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Role"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          height: 40,
                          minWidth: 120,
                        }
                      }}
                    />
                  )}
                  sx={{ minWidth: 120 }}
                />
                
                {/* Clear Filters Button */}
                <Tooltip title="Clear all filters">
                  <IconButton
                    onClick={resetFilters}
                    size="medium"
                    disabled={!searchQuery && filterRole === 'all'}
                    sx={{
                      bgcolor: (searchQuery || filterRole !== 'all') ? 'primary.light' : 'grey.200',
                      color: (searchQuery || filterRole !== 'all') ? 'primary.contrastText' : 'text.secondary',
                      width: 40,
                      height: 40,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText'
                      },
                      transition: 'all 0.2s',
                      '&.Mui-disabled': { bgcolor: 'grey.200', color: 'text.disabled' }
                    }}
                  >
                    <FilterIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Right: Mobile Filter Toggle */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  sx={{ borderRadius: 2, height: 40, textTransform: 'none', minWidth: 'auto', px: 2 }}
                >
                  {filtersOpen ? 'Hide' : 'Filters'}
                </Button>
              </Box>
            </Box>

            {/* Mobile Filter Collapse */}
            <Collapse in={filtersOpen} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
                <Autocomplete
                  size="small"
                  options={roleOptions}
                  value={filterRole}
                  onChange={(e, newValue) => setFilterRole(newValue || 'all')}
                  renderInput={(params) => <TextField {...params} label="Role" size="small" />}
                  fullWidth
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={resetFilters}
                  fullWidth
                  sx={{ borderRadius: 2, height: 40 }}
                >
                  Clear All Filters
                </Button>
              </Box>
            </Collapse>
          </Paper>

          {/* ✅ Filter Chips */}
          {renderFilterChips().length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {renderFilterChips().map((chip, i) => (
                <Chip
                  key={i}
                  label={chip.label}
                  onDelete={chip.onRemove}
                  size="small"
                  onDeleteIcon={<CloseIcon fontSize="small" />}
                />
              ))}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {isMobile ? (
            <Box>
              {clientCards}
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                background: theme.palette.mode === "light" ? "#fff" : "#121212",
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background: theme.palette.action.hover,
                        "& th": { fontWeight: 600 },
                      }}
                    >
                      <TableCell>SL</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>TAX ID</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{tableRows}</TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Fade>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <BusinessIcon sx={{ color: theme.palette.primary.main }} />
          {editing ? "Edit Client" : "Add New Client"}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Divider />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
            <TextField
              label="Client Name *"
              name="name"
              fullWidth
              value={formData.name}
              onChange={handleFormChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={editing && !canUpdate}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              name="email"
              fullWidth
              value={formData.email}
              onChange={handleFormChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={editing && !canUpdate}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Phone"
              name="phone"
              fullWidth
              value={formData.phone}
              onChange={handleFormChange}
              disabled={editing && !canUpdate}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Contact Person"
              name="contact_person"
              fullWidth
              value={formData.contact_person}
              onChange={handleFormChange}
              disabled={editing && !canUpdate}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth margin="dense">
  <InputLabel>Client Role</InputLabel>
  <Select
    name="role"
    value={formData.role}
    onChange={handleFormChange}
    label="Client Role"
  >
    <MenuItem value="">None</MenuItem>
    <MenuItem value="Primary Contact">Primary Contact</MenuItem>
    <MenuItem value="Billing Contact">Billing Contact</MenuItem>
    <MenuItem value="Technical Advisor">Technical Advisor</MenuItem>
    <MenuItem value="Decision Maker">Decision Maker</MenuItem>
    <MenuItem value="Other">Other</MenuItem>
  </Select>
</FormControl>


            <TextField
              label="Tax ID / VAT"
              name="tax_id"
              fullWidth
              value={formData.tax_id}
              onChange={handleFormChange}
              disabled={editing && !canUpdate}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Address"
              name="address"
              fullWidth
              multiline
              rows={2}
              value={formData.address}
              onChange={handleFormChange}
              disabled={editing && !canUpdate}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Billing Address"
              name="billing_address"
              fullWidth
              multiline
              rows={2}
              value={formData.billing_address}
              onChange={handleFormChange}
              disabled={editing && !canUpdate}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={handleClose}
            color="inherit"
            startIcon={<BackIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              saving ||
              (editing && !canUpdate) ||
              (!editing && !canCreate)
            }
            startIcon={saving ? null : <SaveIcon />}
            sx={{
                textTransform: 'none',
                bgcolor: COLORS.secondary,
                '&:hover': {
                  bgcolor: COLORS.primary
                }
              }}
          >
            {saving ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                {editing ? "Updating..." : "Creating..."}
              </>
            ) : editing ? (
              "Update Client"
            ) : (
              "Create Client"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default ClientPage;